import * as yup from "yup";

import { Button, Grid, Typography } from "@mui/material";
import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import AutoComplete from "../../../../components/hookForms/autoComplete";
import DatePickerMui from "../../../../components/hookForms/datePicker";
import FileInputButton from "../../../../components/fileInputButton";
import IGenericResult from "../../../../interfaces/IGenericResult";
import InputNumber from "../../../../components/hookForms/inputNumber";
import { getBase64 } from "../../../../utils/utils";
import messages from "../../../../utils/messages";
import { s3upload } from "../../../../utils/s3";
import serviceFaturaUsina from "../../../../services/serviceFaturaUsina";
import serviceUsina from "../../../../services/serviceUsina";

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    onClose?: any;
}

export default function DialogNew({ open, onClose, onSuccess }: IDialogProps) {
    const [usinaDataOptions, setUsinaDataOptions] = useState<IGenericResult[]>([]);
    const [isDisable, setDisable] = useState(false);

    const validationSchema = yup.object().shape({
        usinaID: yup.string().required(messages.requiredField),
        quantidadeInjetada: yup.string().required(messages.requiredField),
        geracaoConcessionaria: yup.string().required(messages.requiredField),
        saldoAcumulado: yup.string().required(messages.requiredField),
        dataLeituraAnterior: yup.date().required(messages.requiredField).typeError(messages.invalidDate),
        dataLeituraAtual: yup.date().required(messages.requiredField).typeError(messages.invalidDate),
        dataProximaLeitura: yup.date().required(messages.requiredField).typeError(messages.invalidDate),
        dataEmissao: yup.date().required(messages.requiredField).typeError(messages.invalidDate),
        dataVencimento: yup.date().required(messages.requiredField).typeError(messages.invalidDate),
        dataReferencia: yup.date().required(messages.requiredField).typeError(messages.invalidDate),
        valorFatura: yup.string().required(messages.requiredField),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            usinaID: "",
            quantidadeInjetada: "",
            geracaoConcessionaria: "",
            saldoAcumulado: "",
            dataLeituraAnterior: "",
            dataLeituraAtual: "",
            dataProximaLeitura: "",
            dataEmissao: "",
            dataVencimento: "",
            dataReferencia: "",
            valorFatura: "",
        },
    });

    useEffect(() => {
        setUsinaData();
    }, []);

    const setUsinaData = async () => {
        serviceUsina
            .getUsinas()
            .then((result) => {
                setUsinaDataOptions(result?.data);
            })
            .catch(() => {
                setUsinaDataOptions([]);
            });
    };

    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileInput = (e: any) => {
        const extension = e.target.files[0].name.split(".").pop().toLowerCase();

        if (["pdf"].includes(extension)) {
            setSelectedFile(e.target.files[0]);
        } else {
            toast.error("O arquivo selecionado é inválido. É necessário anexar a fatura no formato PDF!");
            setSelectedFile(null);
        }
    };

    const onCancelNew = () => {
        reset();
        setSelectedFile(null);
        onClose(true);
    };

    const onSubmit = (values: any) => {
        setDisable(true);

        if (validate(values, selectedFile)) {
            const newItem = {
                ...values,
                valorFatura: values.valorFatura
                    ? Number(values.valorFatura.replaceAll(".", "").replaceAll(",", "."))
                    : 0,
                saldoAcumulado: values.saldoAcumulado
                    ? Number(values.saldoAcumulado.replaceAll(".", "").replaceAll(",", "."))
                    : 0,
                quantidadeInjetada: values.quantidadeInjetada
                    ? Number(values.quantidadeInjetada.replaceAll(".", "").replaceAll(",", "."))
                    : 0,
                geracaoConcessionaria: values.geracaoConcessionaria
                    ? Number(values.geracaoConcessionaria.replaceAll(".", "").replaceAll(",", "."))
                    : 0,
                dataLeituraAtual: values.dataLeituraAtual ? format(values?.dataLeituraAtual, "yyyy-MM-dd") : "",
                dataEmissao: values.dataEmissao ? format(values?.dataEmissao, "yyyy-MM-dd") : "",
                dataVencimento: values.dataVencimento ? format(values?.dataVencimento, "yyyy-MM-dd") : "",
                dataLeituraAnterior: values.dataLeituraAnterior
                    ? format(values?.dataLeituraAnterior, "yyyy-MM-dd")
                    : "",
                dataProximaLeitura: values.dataProximaLeitura ? format(values?.dataProximaLeitura, "yyyy-MM-dd") : "",
                dataReferencia: values.dataReferencia ? format(values?.dataReferencia, "yyyy-MM-dd") : "",
                cicloFaturamento: values.dataReferencia ? format(values?.dataReferencia, "MM/yyyy") : "",
                statusFaturaUsina: "Aguardando Aprovação",
                usinaID: Number(values.usinaID),
            };

            serviceFaturaUsina
                .create(newItem)
                .then((result) => {
                    if (result) {
                        if (selectedFile) {
                            //Realiza o Upload da Fatura para o S3
                            const bucket = process.env.REACT_APP_S3_BUCKET_FATURAS_USINA || "";

                            getBase64(selectedFile).then((base64) => {
                                s3upload(
                                    bucket,
                                    `${result.data.nomeUsina}/${result.data.faturaUsinaID.toString()}.pdf`,
                                    base64
                                );
                            });
                        }

                        toast.success(messages.createSucess);
                        onClose(true);
                        reset();
                        setSelectedFile(null);
                        onSuccess();
                    }
                })
                .catch((err) => {
                    toast.error(messages.createError, err);
                })
                .finally(() => {
                    setDisable(false);
                });
        } else {
            setDisable(false);
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            open={open}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    Nova Fatura de Usina
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            onCancelNew();
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={12}>
                            <AutoComplete
                                name="usinaID"
                                label="Usina"
                                options={usinaDataOptions}
                                control={control}
                                error={errors}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputNumber
                                name="quantidadeInjetada"
                                label="Quantidade Injetada"
                                control={control}
                                error={errors}
                                decimalScale={0}
                                maxLength={9}
                                noThousandSeparator
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputNumber
                                name="geracaoConcessionaria"
                                label="Geração Concessionária"
                                control={control}
                                error={errors}
                                decimalScale={0}
                                maxLength={9}
                                noThousandSeparator
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputNumber
                                name="saldoAcumulado"
                                label="Saldo"
                                control={control}
                                error={errors}
                                decimalScale={2}
                                maxLength={16}
                            />
                        </Grid>
                    </Grid>
                    <Typography sx={{ fontSize: "24px", color: "#019BBE", mt: 2 }}>Informações Básicas</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                            <DatePickerMui
                                name="dataLeituraAnterior"
                                label="Data da Leitura Anterior"
                                control={control}
                                error={errors}
                                noDateForward={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePickerMui
                                name="dataLeituraAtual"
                                label="Data da Leitura Atual"
                                control={control}
                                error={errors}
                                noDateForward={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePickerMui
                                name="dataProximaLeitura"
                                label="Data da Próxima Leitura"
                                control={control}
                                error={errors}
                                noDateForward={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePickerMui
                                name="dataEmissao"
                                label="Data de Emissão"
                                control={control}
                                error={errors}
                                noDateForward={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePickerMui
                                name="dataVencimento"
                                label="Data de Vencimento"
                                control={control}
                                error={errors}
                                noDateForward={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePickerMui
                                name="dataReferencia"
                                label="Data de Referência"
                                control={control}
                                error={errors}
                                noDateForward={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <InputNumber
                                name="valorFatura"
                                label="Valor da Fatura"
                                control={control}
                                error={errors}
                                decimalScale={2}
                                maxLength={12}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FileInputButton
                                accept="application/pdf"
                                onChange={handleFileInput}
                                selectedFile={selectedFile}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                    <Button
                        onClick={() => {
                            onCancelNew();
                        }}
                        color="warning"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isDisable}
                        style={{ width: "159px", height: "44px" }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

function validate(values: any, selectedFile: any): boolean {
    let dataLeituraAnterior = parse(format(values.dataLeituraAnterior, "dd/MM/yyyy"), "dd/MM/yyyy", new Date());
    let dataLeituraAtual = parse(format(values.dataLeituraAtual, "dd/MM/yyyy"), "dd/MM/yyyy", new Date());
    let dataProximaLeitura = parse(format(values.dataProximaLeitura, "dd/MM/yyyy"), "dd/MM/yyyy", new Date());
    let dataEmissao = parse(format(values.dataEmissao, "dd/MM/yyyy"), "dd/MM/yyyy", new Date());
    let dataVencimento = parse(format(values.dataVencimento, "dd/MM/yyyy"), "dd/MM/yyyy", new Date());

    if (dataLeituraAnterior >= dataLeituraAtual) {
        toast.error("Data leitura atual deve ser maior que a data da leitura anterior.");
        return false;
    }

    if (dataProximaLeitura <= dataLeituraAtual) {
        toast.error("Data da próxima leitura deve ser maior que a data da leitura atual.");
        return false;
    }

    if (dataEmissao <= dataLeituraAtual) {
        toast.error("Data de emissão deve ser maior que a data da leitura atual.");
        return false;
    }

    if (dataVencimento <= dataLeituraAtual) {
        toast.error("Data do vencimento deve ser maior que a data da leitura atual.");
        return false;
    }

    if (!selectedFile) {
        toast.error("É necessário anexar o PDF da fatura.");
        return false;
    }

    return true;
}
